import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { shortAbsenceService } from '../../services/api';
import DepartmentSelector from '../DepartmentSelector';
import SkeletonBlock from '../ui/SkeletonBlock';
import EmptyState from '../ui/EmptyState';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function ShortAbsenceReport() {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [departmentId, setDepartmentId] = useState('');
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await shortAbsenceService.getAll({ year, month, departmentId: departmentId || undefined, status: 1 }); // Approved only
        setAbsences(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load report data', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [year, month, departmentId]);

  // Aggregate by employee
  const employeeSummary = useMemo(() => {
    const map = {};
    absences.forEach(a => {
      if (!map[a.employeeId]) {
        map[a.employeeId] = { name: a.employeeName, department: a.departmentName, totalHours: 0, count: 0 };
      }
      map[a.employeeId].totalHours += a.durationHours || 0;
      map[a.employeeId].count += 1;
    });
    return Object.entries(map)
      .map(([id, data]) => ({ employeeId: id, ...data, equivalentDays: (data.totalHours / 8).toFixed(2) }))
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [absences]);

  const totalHours = employeeSummary.reduce((sum, e) => sum + e.totalHours, 0);
  const totalDays = (totalHours / 8).toFixed(2);

  const handleExport = async () => {
    try {
      const blob = await shortAbsenceService.exportCsv(year, month, departmentId || undefined);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `short-absences-${year}-${String(month).padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error('Export failed', e);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Short Absence Report</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </Select>
          </FormControl>
          <DepartmentSelector value={departmentId} onChange={setDepartmentId} showAll size="small" sx={{ minWidth: 200 }} />
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
            Export CSV
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <SkeletonBlock lines={5} />
        ) : employeeSummary.length === 0 ? (
          <EmptyState title="No Data" description="No approved short absences for this period." />
        ) : (
          <>
            <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
              <Typography variant="body1">
                <strong>Total Hours:</strong> {totalHours}h
              </Typography>
              <Typography variant="body1">
                <strong>Equivalent Work Days:</strong> {totalDays}
              </Typography>
              <Typography variant="body1">
                <strong>Employees:</strong> {employeeSummary.length}
              </Typography>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Total Hours</TableCell>
                    <TableCell align="right">Equivalent Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeSummary.map((emp) => (
                    <TableRow key={emp.employeeId}>
                      <TableCell>{emp.name}</TableCell>
                      <TableCell>{emp.department || '-'}</TableCell>
                      <TableCell align="right">{emp.count}</TableCell>
                      <TableCell align="right">{emp.totalHours}h</TableCell>
                      <TableCell align="right">{emp.equivalentDays}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
}

